import { Component, OnInit, Input } from '@angular/core';
import { StatsService } from '../../../services/stats.service';
import { Observable, Subscription } from 'rxjs';

interface CountryStats {
    total: number;
    today: number;
    future: number;
    delayed: number;
    delayedNoAvailability: number;
    uniqueUsers: number;
    uniqueProfiles: number;
}

interface CompletedReviewCountryStats {
    total: number;
    published: number;
    canceled: number;
    withLocalGuide: number;
    warrantyClaimed: number;
}

interface CompletedReviewPaymentStats {
    credits: number;
    subscription: number;
    freePromo: number;
}

interface CompletedReviewPeriodStats {
    total: number;
    published: number;
    canceled: number;
    withLocalGuide: number;
    warrantyClaimed: number;
    byCountry: { [country: string]: CompletedReviewCountryStats };
    byPaymentMethod: CompletedReviewPaymentStats;
}

interface CompletedReviewStats {
    lastMonth: CompletedReviewPeriodStats;
    lastYear: CompletedReviewPeriodStats;
}

interface AccountStats {
    total: number;
    blocked_percent: number;
    byCountry?: { [country: string]: { total: number; blocked_percent: number; } }
}

interface PublisherStats {
    total: number;
    blocked_percent: number;
    accounts: AccountStats;
}

interface CompanyStats {
    total: number;
    conversion_rate: number;
    profiles: {
        total: number;
        byCountry: { [country: string]: number; }
    }
}

interface PeriodStats {
    last24h: {
        companies: CompanyStats;
        publishers: PublisherStats;
    };
    lastMonth: {
        companies: CompanyStats;
        publishers: PublisherStats;
    };
    lastYear: {
        companies: CompanyStats;
        publishers: PublisherStats;
    };
}

interface CountryEntry {
    country: string;
    total: number;
    today: number;
    future: number;
    delayed: number;
    delayedNoAvailability: number;
    uniqueUsers: number;
    uniqueProfiles: number;
}

interface CountryStatsRow {
    country: string;
    profiles: number;
    gmb: number;
    gmb_blocked_percent: number;
}

interface CompletedReviewCountryRow {
    country: string;
    total: number;
    published: number;
    canceled: number;
    withLocalGuide: number;
    warrantyClaimed: number;
}

type CategoryType = 'reviews' | 'registrations' | 'completedReviews';

@Component({
    selector: 'app-stats',
    templateUrl: './stats.component.html',
    styleUrls: ['./stats.component.scss']
})
export class StatsComponent implements OnInit {

    @Input('load') lazyLoad: Observable<String>;
    private loadSub: Subscription;

    // Estado global
    globalLoading: boolean = false;
      // Control de categorías expandidas
    expandedCategories: { [key in CategoryType]: boolean } = {
        reviews: false,
        registrations: false,
        completedReviews: false
    };

    // Control de carga por categoría
    categoryLoading: { [key in CategoryType]: boolean } = {
        reviews: false,
        registrations: false,
        completedReviews: false
    };

    // Datos cargados por categoría
    categoryData: { [key in CategoryType]: boolean } = {
        reviews: false,
        registrations: false,
        completedReviews: false
    };    // Datos existentes
    stats: any = {};
    registerStats: PeriodStats;
    completedReviewStats: CompletedReviewStats;
    countryStats: CountryEntry[] = [];
    countryColumns: string[] = ['country', 'total', 'uniqueUsers', 'uniqueProfiles', 'today', 'future', 'delayed', 'delayedNoAvailability'];

    // Columnas para las tablas de registros por país
    countryRegisterColumns: string[] = ['country', 'profiles', 'accounts', 'blocked_percent'];
    
    // Columnas para las tablas de reseñas completadas por país
    completedReviewCountryColumns: string[] = ['country', 'total', 'published', 'canceled', 'withLocalGuide', 'warrantyClaimed'];
    
    // Datos procesados para las tablas por país
    last24hStats: CountryStatsRow[] = [];
    lastMonthStats: CountryStatsRow[] = [];
    lastYearStats: CountryStatsRow[] = [];    // Datos procesados para reseñas completadas por país
    completedLastMonthStats: CompletedReviewCountryRow[] = [];
    completedLastYearStats: CompletedReviewCountryRow[] = [];

    // Control del período seleccionado para registros
    selectedPeriod: keyof PeriodStats = 'last24h';
    
    // Control del período seleccionado para reseñas completadas
    selectedCompletedPeriod: keyof CompletedReviewStats = 'lastMonth';

    constructor(
        private statsService: StatsService
    ) { }    ngOnInit() {
        this.lazyLoad.subscribe((tabName) => {
            if (tabName == 'stats') {
                // No cargar automáticamente, solo mostrar el acordeón
            }
        });
    }

    ngOnDestroy() {
        if (this.loadSub) this.loadSub.unsubscribe();
    }    async refreshAllStats() {
        this.globalLoading = true;
        try {
            // Resetear datos cargados
            this.categoryData.reviews = false;
            this.categoryData.registrations = false;
            this.categoryData.completedReviews = false;
            
            // Si alguna categoría está expandida, recargar sus datos
            const promises: Promise<void>[] = [];
            
            if (this.expandedCategories.reviews) {
                promises.push(this.loadPendingStats());
            }
            
            if (this.expandedCategories.registrations) {
                promises.push(this.loadRegisterStats());
            }

            if (this.expandedCategories.completedReviews) {
                promises.push(this.loadCompletedReviewStats());
            }
            
            await Promise.all(promises);
        } catch (error) {
            console.error('Error refreshing stats:', error);
        }
        this.globalLoading = false;
    }

    async loadCategoryStats(category: CategoryType) {
        // Si ya se cargaron los datos para esta categoría, no volver a cargar
        if (this.categoryData[category]) {
            return;
        }

        this.categoryLoading[category] = true;
        this.expandedCategories[category] = true;        try {
            switch (category) {
                case 'reviews':
                    await this.loadPendingStats();
                    break;
                case 'registrations':
                    await this.loadRegisterStats();
                    break;
                case 'completedReviews':
                    await this.loadCompletedReviewStats();
                    break;
            }
            this.categoryData[category] = true;
        } catch (error) {
            console.error(`Error loading ${category} stats:`, error);
        } finally {
            this.categoryLoading[category] = false;
        }
    }

    private async loadPendingStats() {
        try {
            const response: any = await this.statsService.getPendingReviewStats();
            this.stats = response.stats;
            
            // Procesar las estadísticas por país
            const countryEntries = Object.entries(this.stats.byCountry || {});
            
            // Separar países con 1 reseña y el resto
            const singleReviews = countryEntries.filter(([_, stats]) => (stats as CountryStats).total === 1);
            const multipleReviews = countryEntries.filter(([_, stats]) => (stats as CountryStats).total > 1);

            // Ordenar países con más de 1 reseña de mayor a menor
            const sortedCountries = multipleReviews
                .map(([country, stats]) => ({
                    country,
                    ...(stats as CountryStats)
                }))
                .sort((a, b) => b.total - a.total);            // Si hay países con 1 reseña, añadir fila agrupada al final
            if (singleReviews.length > 0) {
                const othersStats = singleReviews.reduce((acc: CountryStats, [_, stats]) => ({
                    total: acc.total + (stats as CountryStats).total,
                    today: acc.today + (stats as CountryStats).today,
                    future: acc.future + (stats as CountryStats).future,
                    delayed: acc.delayed + (stats as CountryStats).delayed,
                    delayedNoAvailability: acc.delayedNoAvailability + (stats as CountryStats).delayedNoAvailability,
                    uniqueUsers: acc.uniqueUsers + (stats as CountryStats).uniqueUsers,
                    uniqueProfiles: acc.uniqueProfiles + (stats as CountryStats).uniqueProfiles
                }), { 
                    total: 0, 
                    today: 0, 
                    future: 0, 
                    delayed: 0,
                    delayedNoAvailability: 0,
                    uniqueUsers: 0,
                    uniqueProfiles: 0 
                });

                sortedCountries.push({
                    country: `Otros países con 1 reseña (${singleReviews.length})`,
                    ...othersStats
                });
            }

            this.countryStats = sortedCountries;
        } catch (error) {
            console.error('Error loading pending stats:', error);
            throw error;
        }
    }

    private async loadRegisterStats() {
        try {
            const response = await this.statsService.getAccountRegisters() as PeriodStats;
            this.registerStats = response;
            
            // Procesar datos por país para cada período
            const periods = ['last24h', 'lastMonth', 'lastYear'] as Array<keyof PeriodStats>;
            
            // Procesamos cada período
            periods.forEach(period => {
                const stats = this.getCountryStats(response[period]);
                // Ordenar por número de cuentas GMB de mayor a menor
                this[`${period}Stats`] = stats.sort((a, b) => b.gmb - a.gmb);
            });        } catch (error) {
            console.error('Error loading register stats:', error);
            throw error;
        }
    }    private async loadCompletedReviewStats() {
        try {
            const response: any = await this.statsService.getCompletedReviewStats();
            // La respuesta viene envuelta en un objeto 'stats'
            this.completedReviewStats = response.stats;
            
            // Procesar datos por país para cada período (sin last24h)
            const periods = ['lastMonth', 'lastYear'] as Array<keyof CompletedReviewStats>;
            
            // Procesamos cada período
            periods.forEach(period => {
                const stats = this.getCompletedReviewCountryStats(response.stats[period]);
                // Ordenar por total de reseñas completadas de mayor a menor
                this[`completed${period.charAt(0).toUpperCase() + period.slice(1)}Stats`] = stats.sort((a, b) => b.total - a.total);
            });

        } catch (error) {
            console.error('Error loading completed review stats:', error);
            throw error;
        }
    }

    private getCompletedReviewCountryStats(periodStats: CompletedReviewPeriodStats): CompletedReviewCountryRow[] {
        if (!periodStats || !periodStats.byCountry) {
            return [];
        }

        return Object.entries(periodStats.byCountry).map(([country, stats]) => ({
            country,
            total: stats.total,
            published: stats.published,
            canceled: stats.canceled,
            withLocalGuide: stats.withLocalGuide,
            warrantyClaimed: stats.warrantyClaimed
        }));
    }

    private getCountryStats(periodStats: { companies: CompanyStats; publishers: PublisherStats }): CountryStatsRow[] {
        const countries = new Set<string>();
        
        // Recolectar países de perfiles y cuentas
        Object.keys(periodStats.companies.profiles.byCountry || {}).forEach(country => countries.add(country));
        Object.keys(periodStats.publishers.accounts.byCountry || {}).forEach(country => countries.add(country));

        // Generar estadísticas por país
        return Array.from(countries).map(country => {
            const profiles = periodStats.companies.profiles.byCountry[country] || 0;
            const countryAccounts = periodStats.publishers.accounts && 
                                  periodStats.publishers.accounts.byCountry && 
                                  periodStats.publishers.accounts.byCountry[country];
            const accounts = countryAccounts ? countryAccounts.total : 0;
            const blockedPercent = countryAccounts ? countryAccounts.blocked_percent : 0;

            return {
                country,
                profiles,
                gmb: accounts,
                gmb_blocked_percent: blockedPercent
            };
        });
    }

    onPeriodChange(event: any) {
        this.selectedPeriod = event.value;
    }

    getCurrentPeriodStats() {
        if (!this.registerStats) return null;
        return this.registerStats[this.selectedPeriod];
    }

    getCurrentTableData(): CountryStatsRow[] {
        switch (this.selectedPeriod) {
            case 'last24h':
                return this.last24hStats || [];
            case 'lastMonth':
                return this.lastMonthStats || [];
            case 'lastYear':
                return this.lastYearStats || [];
            default:
                return [];
        }
    }

    getPeriodTitle(): string {
        switch (this.selectedPeriod) {
            case 'last24h':
                return 'Últimas 24 horas';
            case 'lastMonth':
                return 'Último mes';
            case 'lastYear':
                return 'Último año';
            default:
                return '';
        }
    }    getConversionDescription(): string {
        const stats = this.getCurrentPeriodStats();
        if (!stats || !stats.companies) return '';
        
        const total = stats.companies.total;
        const conversionRate = stats.companies.conversion_rate || 0;
        const converted = Math.round((total * conversionRate) / 100);
        
        return `${converted} de ${total} empresas han realizado al menos una compra`;
    }

    // Métodos auxiliares para evitar repetir verificaciones en el template
    getCompaniesTotal(): number {
        const stats = this.getCurrentPeriodStats();
        return stats && stats.companies ? stats.companies.total : 0;
    }

    getConversionRate(): number {
        const stats = this.getCurrentPeriodStats();
        return stats && stats.companies ? stats.companies.conversion_rate : 0;
    }

    getProfilesTotal(): number {
        const stats = this.getCurrentPeriodStats();
        return stats && stats.companies && stats.companies.profiles ? stats.companies.profiles.total : 0;
    }

    getPublishersTotal(): number {
        const stats = this.getCurrentPeriodStats();
        return stats && stats.publishers ? stats.publishers.total : 0;
    }

    getPublishersBlockedPercent(): number {
        const stats = this.getCurrentPeriodStats();
        return stats && stats.publishers ? stats.publishers.blocked_percent : 0;
    }

    getAccountsTotal(): number {
        const stats = this.getCurrentPeriodStats();
        return stats && stats.publishers && stats.publishers.accounts ? stats.publishers.accounts.total : 0;
    }    getAccountsBlockedPercent(): number {
        const stats = this.getCurrentPeriodStats();
        return stats && stats.publishers && stats.publishers.accounts ? stats.publishers.accounts.blocked_percent : 0;
    }

    // Métodos para reseñas completadas
    onCompletedPeriodChange(event: any) {
        this.selectedCompletedPeriod = event.value;
    }

    getCurrentCompletedPeriodStats() {
        if (!this.completedReviewStats) return null;
        return this.completedReviewStats[this.selectedCompletedPeriod];
    }    getCurrentCompletedTableData(): CompletedReviewCountryRow[] {
        switch (this.selectedCompletedPeriod) {
            case 'lastMonth':
                return this.completedLastMonthStats || [];
            case 'lastYear':
                return this.completedLastYearStats || [];
            default:
                return [];
        }
    }

    getCompletedPeriodTitle(): string {
        switch (this.selectedCompletedPeriod) {
            case 'lastMonth':
                return 'Último mes';
            case 'lastYear':
                return 'Último año';
            default:
                return '';
        }
    }

    // Métodos auxiliares para reseñas completadas
    getCompletedTotal(): number {
        const stats = this.getCurrentCompletedPeriodStats();
        return stats ? stats.total : 0;
    }

    getCompletedPublished(): number {
        const stats = this.getCurrentCompletedPeriodStats();
        return stats ? stats.published : 0;
    }

    getCompletedCanceled(): number {
        const stats = this.getCurrentCompletedPeriodStats();
        return stats ? stats.canceled : 0;
    }

    getCompletedWithLocalGuide(): number {
        const stats = this.getCurrentCompletedPeriodStats();
        return stats ? stats.withLocalGuide : 0;
    }

    getCompletedWarrantyClaimed(): number {
        const stats = this.getCurrentCompletedPeriodStats();
        return stats ? stats.warrantyClaimed : 0;
    }

    getCompletedCreditsPayments(): number {
        const stats = this.getCurrentCompletedPeriodStats();
        return stats && stats.byPaymentMethod ? stats.byPaymentMethod.credits : 0;
    }

    getCompletedSubscriptionPayments(): number {
        const stats = this.getCurrentCompletedPeriodStats();
        return stats && stats.byPaymentMethod ? stats.byPaymentMethod.subscription : 0;
    }    getCompletedFreePromoPayments(): number {
        const stats = this.getCurrentCompletedPeriodStats();
        return stats && stats.byPaymentMethod ? stats.byPaymentMethod.freePromo : 0;
    }

    // Métodos para calcular porcentajes de reseñas completadas
    getCompletedPublishedPercent(): number {
        const total = this.getCompletedTotal();
        const published = this.getCompletedPublished();
        return total > 0 ? (published / total) * 100 : 0;
    }

    getCompletedCanceledPercent(): number {
        const total = this.getCompletedTotal();
        const canceled = this.getCompletedCanceled();
        return total > 0 ? (canceled / total) * 100 : 0;
    }

    getCompletedWithLocalGuidePercent(): number {
        const total = this.getCompletedTotal();
        const withLocalGuide = this.getCompletedWithLocalGuide();
        return total > 0 ? (withLocalGuide / total) * 100 : 0;
    }

    getCompletedWarrantyClaimedPercent(): number {
        const total = this.getCompletedTotal();
        const warrantyClaimed = this.getCompletedWarrantyClaimed();
        return total > 0 ? (warrantyClaimed / total) * 100 : 0;
    }

    getCompletedCreditsPaymentsPercent(): number {
        const total = this.getCompletedTotal();
        const credits = this.getCompletedCreditsPayments();
        return total > 0 ? (credits / total) * 100 : 0;
    }

    getCompletedSubscriptionPaymentsPercent(): number {
        const total = this.getCompletedTotal();
        const subscription = this.getCompletedSubscriptionPayments();
        return total > 0 ? (subscription / total) * 100 : 0;
    }

    getCompletedFreePromoPaymentsPercent(): number {
        const total = this.getCompletedTotal();
        const freePromo = this.getCompletedFreePromoPayments();
        return total > 0 ? (freePromo / total) * 100 : 0;
    }
}
